import React from 'react';
import dayjs from 'dayjs';
import 'dayjs/locale/ja';

// 日本語ロケールを設定
dayjs.locale('ja');

// 表示形式の定数
export const DATE_DISPLAY_TYPE = {
  FULL_DATE: 'FULL_DATE',        // YYYY年M月D日（曜日）
  FULL_DATE_TIME: 'FULL_DATE_TIME', // YYYY年M月D日（曜日） H:mm
  SHORT_DATE_TIME: 'SHORT_DATE_TIME', // M月D日（曜日）H:mm （同年なら年省略）
  DATE_TIME: 'DATE_TIME',        // YYYY/M/D H:mm or M/D H:mm
  DATE_ONLY: 'DATE_ONLY',        // YYYY/M/D or M/D（同年は年省略、時刻なし、曜日なし）
  RELATIVE_DAYS: 'RELATIVE_DAYS', // あとX日 or X日経過
  ADAPTIVE: 'ADAPTIVE',          // 状況に応じた表示
  DELIVERY_SCHEDULE: 'DELIVERY_SCHEDULE', // M月D日（曜日）H:mm 納品予定 or 本日 H:mm 納品
  DEADLINE_COUNTDOWN: 'DEADLINE_COUNTDOWN', // 納期まであとX日とHH:MM or 本日 H:mm 提出
} as const;

// 型定義
export type DateDisplayType = typeof DATE_DISPLAY_TYPE[keyof typeof DATE_DISPLAY_TYPE];

/**
 * 日時を soremo 共通の表記に整形して**文字列で**返す。
 *
 * 月・日・時はゼロ埋めしない（`7/4 9:05`）。同年なら年を省略する。
 *
 * 表示は原則 `DateDisplay` コンポーネントを使うこと。この関数は、
 * テンプレート文字列に埋め込む場合や TSV / クリップボードへ書き出す場合の
 * ように **React 要素を置けない箇所**のために公開している。ページ側で
 * `dayjs(...).format("YYYY/MM/DD HH:mm")` のように独自に組み立てると
 * 画面ごとにゼロ埋めの有無がばらつくため、必ずここを経由する。
 *
 * 日付として解釈できない値（空・不正）は空文字を返す。
 */
export const formatDateText = (
  date: string | number | Date | null | undefined,
  type: DateDisplayType = DATE_DISPLAY_TYPE.FULL_DATE,
): string => {
  if (date === null || date === undefined || date === '') return '';
  const dateObj = dayjs(date);
  if (!dateObj.isValid()) return '';
  const today = dayjs();

  const formatFullDate = () => {
    const year = dateObj.year();
    const currentYear = today.year();
    const month = dateObj.format('M'); // 頭の0を除去
    const day = dateObj.format('D');   // 頭の0を除去
    const weekday = dateObj.format('dd')[0]; // 曜日の最初の1文字を取得

    return currentYear === year
      ? `${month}月${day}日（${weekday}）`
      : `${year}年${month}月${day}日（${weekday}）`;
  };

  const formatDateTime = () => {
    const currentYear = today.year();
    const dateYear = dateObj.year();
    const month = dateObj.format('M');
    const day = dateObj.format('D');
    const time = dateObj.format('H:mm');

    return currentYear === dateYear
      ? `${month}/${day} ${time}`
      : `${dateYear}/${month}/${day} ${time}`;
  };

  // 時刻も曜日も付けない、最短の日付表記。
  // - 同年:  `M/D`        例) `5/20`
  // - 他年:  `YYYY/M/D`   例) `2027/1/3`
  // 当日でも `H:mm` に変わらないので、複数日を並べる用途 (NG 日リスト等) で安定する。
  const formatDateOnly = () => {
    const currentYear = today.year();
    const dateYear = dateObj.year();
    const md = dateObj.format('M/D');
    return currentYear === dateYear ? md : `${dateYear}/${md}`;
  };

  const formatFullDateTime = () => {
    const year = dateObj.year();
    const currentYear = today.year();
    const month = dateObj.format('M'); // 頭の0を除去
    const day = dateObj.format('D');   // 頭の0を除去
    const weekday = dateObj.format('dd')[0]; // 曜日の最初の1文字を取得
    const time = dateObj.format('H:mm');

    return currentYear === year
      ? `${month}月${day}日（${weekday}） ${time}`
      : `${year}年${month}月${day}日（${weekday}） ${time}`;
  };

  const formatRelativeDays = () => {
    const diffDays = dateObj.diff(today, 'day');
    if (diffDays === 0) {
      return '本日';
    }
    return diffDays > 0
      ? `あと${diffDays}日`
      : `${Math.abs(diffDays)}日経過`;
  };

  const formatDeliverySchedule = () => {
    const diffDays = dateObj.startOf('day').diff(today.startOf('day'), 'day');
    const time = dateObj.format('H:mm');
    if (diffDays === 0) {
      return `本日 ${time} 納品`;
    }
    const month = dateObj.format('M');
    const day = dateObj.format('D');
    const weekday = dateObj.format('dd')[0];
    return `${month}月${day}日（${weekday}）${time} 納品予定`;
  };

  const formatDeadlineCountdown = () => {
    const totalMinutes = dateObj.diff(today, 'minute');
    const days = Math.floor(totalMinutes / (60 * 24));
    const hours = Math.floor((totalMinutes % (60 * 24)) / 60);
    const mins = totalMinutes % 60;
    if (days <= 0 && hours <= 0 && mins <= 0) {
      return `本日 ${dateObj.format('H:mm')} 提出`;
    }
    const hh = String(hours).padStart(2, '0');
    const mm = String(mins).padStart(2, '0');
    if (days === 0) {
      return `納期まであと${hh}:${mm}`;
    }
    if (hours === 0 && mins === 0) {
      return `納期まであと${days}日`;
    }
    return `納期まであと${days}日と${hh}:${mm}`;
  };

  const formatAdaptive = () => {
    const diffDays = dateObj.diff(today, 'day');

    if (diffDays === 0) {
      return dateObj.format('H:mm');
    }

    if (diffDays > -7 && diffDays < 0) {
      return dateObj.format('dd')[0];
    }

    if (dateObj.year() !== today.year()) {
      return `${dateObj.year()}/${dateObj.format('M/D')}`;
    }

    return dateObj.format('M/D');
  };

  const formatShortDateTime = () => {
    const year = dateObj.year();
    const currentYear = today.year();
    const month = dateObj.format('M');
    const day = dateObj.format('D');
    const weekday = dateObj.format('dd')[0];
    const time = dateObj.format('H:mm');

    return currentYear === year
      ? `${month}月${day}日（${weekday}）${time}`
      : `${year}年${month}月${day}日（${weekday}）${time}`;
  };

  switch (type) {
    case DATE_DISPLAY_TYPE.FULL_DATE:
      return formatFullDate();
    case DATE_DISPLAY_TYPE.FULL_DATE_TIME:
      return formatFullDateTime();
    case DATE_DISPLAY_TYPE.SHORT_DATE_TIME:
      return formatShortDateTime();
    case DATE_DISPLAY_TYPE.DATE_TIME:
      return formatDateTime();
    case DATE_DISPLAY_TYPE.DATE_ONLY:
      return formatDateOnly();
    case DATE_DISPLAY_TYPE.RELATIVE_DAYS:
      return formatRelativeDays();
    case DATE_DISPLAY_TYPE.ADAPTIVE:
      return formatAdaptive();
    case DATE_DISPLAY_TYPE.DELIVERY_SCHEDULE:
      return formatDeliverySchedule();
    case DATE_DISPLAY_TYPE.DEADLINE_COUNTDOWN:
      return formatDeadlineCountdown();
    default:
      return formatFullDate();
  }
};

interface DateDisplayProps {
  /** 日時。空・null・不正な値は何も描画しない。 */
  date: string | null | undefined;
  type?: DateDisplayType;
}

const DateDisplay: React.FC<DateDisplayProps> = ({ date, type = DATE_DISPLAY_TYPE.FULL_DATE }) => (
  <span style={{ whiteSpace: 'nowrap' }}>{formatDateText(date, type)}</span>
);

export default DateDisplay;
